/**
 * Marketing-only Analytics Service
 * Simplified for TrainZilla landing page and lead gen.
 *
 * GA4 base config lives in index.html (gtag with G-9Q8E40LN2K). This module
 * only sends events. Page views are handled by GA4 Enhanced Measurement
 * ("page changes based on browser history events"), so we do NOT send manual
 * page_view events here (that would double-count).
 */

export const GA_MEASUREMENT_ID = 'G-9Q8E40LN2K';

const hasGtag = (): boolean =>
  typeof window !== 'undefined' && typeof (window as any).gtag === 'function';

// Initialize Google Analytics (base tag is in index.html)
export const initializeGA = () => {
  if (hasGtag() && typeof console !== 'undefined') {
    // no-op: base config already loaded from index.html
  }
};

// Track page view — kept for callers that need an explicit virtual pageview.
// Not called on route change (see file header).
export const trackPageView = (pagePath: string, pageTitle?: string) => {
  if (hasGtag()) {
    (window as any).gtag('event', 'page_view', {
      page_path: pagePath,
      page_title: pageTitle || (typeof document !== 'undefined' ? document.title : undefined),
      page_location: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  }
};

// Generic event tracking
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (hasGtag()) {
    (window as any).gtag('event', eventName, parameters || {});
  }
};

/**
 * Meta Pixel (base tag + PageView in index.html; id 1103728861266831).
 * Only fire *standard* Meta events here so they map to conversions in Ads
 * Manager without a custom-conversion rule. Registration / Subscribe happen on
 * app.trainzilla.in and are sent server-side via CAPI, not from this module.
 */
const hasFbq = (): boolean =>
  typeof window !== 'undefined' && typeof (window as any).fbq === 'function';

export const metaTrack = (event: string, params?: Record<string, any>) => {
  if (hasFbq()) (window as any).fbq('track', event, params || {});
};

/* ------------------------------------------------------------------ *
 * GA4 standard conversion events
 * These use GA4's *recommended* event names so they can be marked as
 * Key Events in the GA4 UI with one click (Admin → Events → toggle).
 *
 * Blog content funnel (traffic from SEO articles):
 *   funnel_stage_view   — fired once per article load, carries the stage
 *   generate_lead       — soft capture: inline newsletter form on the article
 *   sign_up_click       — hard CTA: "Start free" block → app.trainzilla.in
 *   (then app-side activation, a separate GA4 property)
 * Mark generate_lead and sign_up as Key Events for the funnel exploration (sign_up itself
 * comes from the app, on real account creation).
 * ------------------------------------------------------------------ */

type LeadContext = {
  /** stable identifier for the form / surface, e.g. "contact_us", "contact_sales" */
  form_id: string;
  /** how the lead came in, e.g. "contact_form", "sales_modal", "webinar", "newsletter" */
  method?: string;
  /** monetary value used for conversion modelling (relative lead quality, INR) */
  value?: number;
  currency?: string;
  [key: string]: any;
};

/** GA4 recommended event: fire on every successful lead-gen form submission. */
export const trackLead = ({
  form_id,
  method = form_id,
  value = 0,
  currency = 'INR',
  ...rest
}: LeadContext) => {
  trackEvent('generate_lead', { form_id, method, value, currency, ...rest });
  metaTrack('Lead', { content_name: form_id, ...(value ? { value, currency } : {}) });
};

/**
 * Fire when a visitor clicks a "Start free" / register CTA that leaves for the app.
 * This is a click, not a signup: the GA4 `sign_up` event (a Key Event that Google Ads imports
 * as a conversion) fires from the app itself when the account is actually created, and both
 * hosts share one GA4 property, so firing `sign_up` here as well counted every click as a signup.
 */
export const trackSignUp = (method: string = 'website', extra?: Record<string, any>) => {
  trackEvent('sign_up_click', { method, ...extra });
};

/** GA4 recommended event: fire when a visitor moves toward a paid plan. */
export const trackBeginCheckout = (plan: string, value?: number) => {
  trackEvent('begin_checkout', { currency: 'INR', value: value ?? 0, plan, items: [{ item_name: plan }] });
};

/** Outbound / CTA click. Use for links that leave the marketing site. */
export const trackCtaClick = (label: string, extra?: Record<string, any>) => {
  trackEvent('cta_click', { cta_label: label, ...extra });
  // Clicks that open the sales booking page (labels like gyms_book_demo, book_call,
  // switch_guide_book_call). A separate event so GA4 / Google Ads can use it as a
  // conversion without matching on cta_label parameters.
  if (/(^|_)book(_|$)/.test(label)) {
    trackEvent('book_demo_click', { cta_label: label, ...extra });
  }
};

/** Top of the blog content funnel — fire once when an article page mounts. */
export const trackFunnel = (stage: string, article: string) => {
  trackEvent('funnel_stage_view', { stage, article });
};

// Business-specific trackers (thin wrappers, kept for back-compat)
export const trackBusinessEvent = {
  signUp: (plan: string) => trackEvent('sign_up_click', { plan }),
  login: () => trackEvent('login_click'),
  logout: () => trackEvent('logout_click'),
  scheduleDemo: () => trackEvent('schedule_demo_click'),
  watchDemo: () => trackEvent('watch_demo_click'),
  startTrial: (plan: string) => trackEvent('start_trial_click', { plan }),
  contactSales: () => trackEvent('contact_sales_click'),
  blogPost: (title: string) => trackEvent('blog_view', { title }),
  webinarRegistration: (title: string) => trackEvent('webinar_signup', { title }),
  trainingEnrollment: (title: string) => trackEvent('training_enroll', { title }),
  chatBotInteraction: (action: string, metadata?: any) => trackEvent('chatbot_interact', { action, ...metadata }),
  helpCenterVisit: () => trackPageView('/help-center'),
  manualSupportRequest: (data: any) => trackEvent('support_request', data),
  n8nWebhookCall: (status: any, id: any, err?: any) => trackEvent('webhook_call', { status, id, err }),
  chatMessageSent: (data: any) => trackEvent('chat_msg', data),
  viewPricing: () => trackEvent('view_pricing'),
  selectPlan: (plan: string) => trackEvent('select_plan', { plan }),
  dashboardAction: (action: string, section: string) => trackEvent('dashboard_nav', { action, section }),
  clientManagement: (action: string) => trackEvent('client_mgmt', { action }),
  workoutCreation: () => trackEvent('workout_create'),
  businessMetricsView: () => trackEvent('metrics_view'),
  revenueAnalytics: () => trackEvent('revenue_view'),
  pageLoadTime: (page: string, time: number) => trackEvent('performance', { page, time }),
  error: (type: string, msg: string) => trackEvent('error', { type, msg }),
};

// Ecommerce tracking (minimal)
export const trackEcommerce = {
  viewItem: (itemName: string, price: number) => trackEvent('view_item', { item_name: itemName, value: price }),
  addToCart: (itemName: string, price: number) => trackEvent('add_to_cart', { item_name: itemName, value: price }),
  purchase: (id: string, val: number, items: any[]) => trackEvent('purchase', { transaction_id: id, value: val, items }),
};

// User properties
export const setUserProperties = (properties: any) => {
  if (hasGtag()) {
    (window as any).gtag('set', 'user_properties', properties);
  }
};
